<template>
  <v-dialog
    :model-value="isVisible"
    width="1000"
    @close="$emit('close')"
    persistent
    data-testid="yard-legend-dialog">
    <v-card class="py-4 px-6">
      <v-card-title class="d-flex flex-row">
        <h4 class="text-capitalize mb-6">
          <span>{{ t('yard.dialogs.yardViewLegendDialog.headers.yardManagementCaptions') }}</span>
        </h4>
        <v-spacer></v-spacer>
        <v-btn
          icon="mdi-close"
          variant="text"
          density="comfortable"
          @click="$emit('close')"></v-btn>
      </v-card-title>
      <v-card-text>
        <v-row>
          <v-col cols="6">
            <svg-truck-asset-container
              :rotation="270"
              class="fix-truck-rotation mb-2"
              :border-color="truckThemeColors.blue.borderColor"
              :background-color="truckThemeColors.blue.backgroundColor" />
            <p class="mb-6">{{ t('yard.dialogs.yardViewLegendDialog.messages.liveLoad') }}</p>

            <svg-truck-asset-container-striped
              :rotation="270"
              class="fix-truck-rotation mb-2"
              :border-color="truckThemeColors.blue.borderColor"
              :background-color="truckThemeColors.blue.backgroundColor" />
            <p class="mb-6">
              {{
                t('yard.dialogs.yardViewLegendDialog.messages.truckWithTrailerOrContainerAttached')
              }}
            </p>

            <svg-truck
              :rotation="270"
              :border-color="truckThemeColors.blue.borderColor"
              :background-color="truckThemeColors.blue.backgroundColor"
              class="fix-truck-rotation mb-2" />
            <p class="mb-6">
              {{
                t(
                  'yard.dialogs.yardViewLegendDialog.messages.tractorWithNoTrailerOrContainerAttached'
                )
              }}
            </p>

            <svg-asset-container :rotation="270" class="fix-truck-rotation mb-2" />
            <p class="mb-6">
              {{
                t(
                  'yard.dialogs.yardViewLegendDialog.messages.detachedTrailerContainerOrOversizedFreight'
                )
              }}
            </p>

            <div class="d-flex flex-row gap-5">
              <svg-truck-asset-container
                :rotation="270"
                class="fix-truck-rotation mb-2"
                :border-color="truckThemeColors.grey.borderColor"
                :background-color="truckThemeColors.grey.backgroundColor" />

              <svg-truck-asset-container-striped
                :rotation="270"
                class="fix-truck-rotation mb-2"
                :border-color="truckThemeColors.grey.borderColor"
                :background-color="truckThemeColors.grey.backgroundColor" />

              <svg-truck
                :rotation="270"
                :border-color="truckThemeColors.grey.borderColor"
                :background-color="truckThemeColors.grey.backgroundColor"
                class="fix-truck-rotation mb-2" />
            </div>
            <p class="mb-6">
              {{ t('yard.dialogs.yardViewLegendDialog.messages.noScheduledAppointment') }}
            </p>

            <div class="d-flex flex-row gap-5">
              <svg-truck-asset-container
                :rotation="270"
                class="fix-truck-rotation mb-2"
                :border-color="truckThemeColors.red.borderColor"
                :background-color="truckThemeColors.red.backgroundColor" />

              <svg-truck-asset-container-striped
                :rotation="270"
                class="fix-truck-rotation mb-2"
                :border-color="truckThemeColors.red.borderColor"
                :background-color="truckThemeColors.red.backgroundColor" />

              <svg-truck
                :rotation="270"
                :border-color="truckThemeColors.red.borderColor"
                :background-color="truckThemeColors.red.backgroundColor"
                class="fix-truck-rotation mb-2" />
            </div>
            <p class="mb-6">
              {{ t('yard.dialogs.yardViewLegendDialog.messages.pastTheAppointmentTime') }}
            </p>
          </v-col>
          <v-divider vertical />
          <v-col cols="6" class="d-flex flex-column gap-5">
            <div>
              <v-icon size="small">
                {{ getSpotIcon(SpotType.PARKING) }}
              </v-icon>
              <span class="ml-2">
                {{ t('yard.dialogs.yardViewLegendDialog.messages.parkingSpot') }}
              </span>
            </div>
            <div>
              <v-icon size="small">
                {{ getSpotIcon(SpotType.DOCKING) }}
              </v-icon>
              <span class="ml-2">
                {{ t('yard.dialogs.yardViewLegendDialog.messages.dockingSpot') }}
              </span>
            </div>
            <div>
              <v-icon size="small" :color="getLoadTypeDirectionColor(LoadTypeDirection.Inbound)">
                {{ getAssignedSpotIcon(LoadTypeDirection.Inbound) }}
              </v-icon>
              <span class="ml-2">
                {{ t('yard.dialogs.yardViewLegendDialog.messages.inboundLoadType') }}
              </span>
            </div>
            <div>
              <v-icon size="small" :color="getLoadTypeDirectionColor(LoadTypeDirection.Outbound)">
                {{ getAssignedSpotIcon(LoadTypeDirection.Outbound) }}
              </v-icon>
              <span class="ml-2">
                {{ t('yard.dialogs.yardViewLegendDialog.messages.outboundLoadType') }}
              </span>
            </div>
            <div v-for="eventType in assetContainerLegendItems" :key="eventType">
              <v-icon size="small" :color="getAssetContainerStatusColor(eventType)">
                {{ getAssetContainerStatusIcon(eventType) }}
              </v-icon>
              <span class="ml-2">
                {{ t(`core.yard.assetContainerEvent.types.${camelCase(eventType)}`) }}
              </span>
            </div>
            <div class="legend-item">
              <v-icon size="small" color="info">mdi-calendar</v-icon>
              <span class="ml-2">
                {{ t('yard.dialogs.yardViewLegendDialog.messages.pickupScheduled') }}
              </span>
            </div>
          </v-col>
        </v-row>
      </v-card-text>
      <v-divider></v-divider>
      <v-card-actions class="pa-4">
        <v-spacer></v-spacer>

        <v-btn variant="outlined" @click="$emit('close')" data-testid="yard-legend-dialog-close">
          {{ t('yard.dialogs.yardViewLegendDialog.buttons.gotIt') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { defineComponent, computed } from 'vue';
import { AssetContainerEventType, SpotType, LoadTypeDirection } from '@nova/core';
import useYard from '@/modules/yard/composables/useYard';
import SvgTruck from '@/components/svgs/SvgTruck.vue';
import SvgAssetContainer from '@/components/svgs/SvgAssetContainer.vue';
import SvgTruckAssetContainer from '@/components/svgs/SvgTruckAssetContainer.vue';
import SvgTruckAssetContainerStriped from '@/components/svgs/SvgTruckAssetContainerStriped.vue';
import { useI18n } from 'vue-i18n';
import { camelCase } from 'lodash';

export default defineComponent({
  name: 'YardViewLegendDialog',
  methods: { camelCase },
  components: {
    SvgTruck,
    SvgTruckAssetContainer,
    SvgTruckAssetContainerStriped,
    SvgAssetContainer
  },
  emits: ['close'],
  props: {
    isVisible: {
      type: Boolean,
      default: false
    }
  },
  setup() {
    const { t } = useI18n();
    const yardStore = useYard();

    const assetContainerLegendItems = computed(() => {
      const items = [];
      for (const eventType of Object.values(AssetContainerEventType)) {
        if (eventType === AssetContainerEventType.Departed) {
          continue;
        }

        if (eventType === AssetContainerEventType.Arrived) {
          items.push('Arrived/Waiting');
        } else {
          items.push(eventType);
        }
      }
      return items;
    });

    return {
      assetContainerLegendItems,
      truckThemeColors: yardStore.truckThemeColors,
      getAssetContainerStatusColor: yardStore.getAssetContainerStatusColor,
      getAssetContainerStatusIcon: yardStore.getAssetContainerStatusIcon,
      getLoadTypeDirectionColor: yardStore.getLoadTypeDirectionColor,
      getAssignedSpotIcon: yardStore.getAssignedSpotIcon,
      getSpotIcon: yardStore.getSpotIcon,
      SpotType,
      LoadTypeDirection,
      t
    };
  }
});
</script>
